import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard,
  FileText,
  PenLine,
  Globe,
  ArrowRightLeft,
  History,
  LogOut,
  ChevronRight,
} from 'lucide-react';
import logo from '@/assets/xperfomax-logo.png';

interface NavItem {
  title: string;
  href: string;
  icon: React.ElementType;
  requiredRole?: 'admin' | 'editor' | 'viewer';
  description?: string;
}

const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/admin',
    icon: LayoutDashboard,
    description: 'Overview & stats',
  },
  {
    title: 'Page Content',
    href: '/admin/content',
    icon: PenLine,
    requiredRole: 'editor',
    description: 'Static pages',
  },
  {
    title: 'Content Items',
    href: '/admin/content-items',
    icon: FileText,
    requiredRole: 'editor',
    description: 'Blog, case studies',
  },
  {
    title: 'SEO Hygiene',
    href: '/admin/seo-hygiene',
    icon: Globe,
    requiredRole: 'editor',
    description: 'Health checks',
  },
  {
    title: 'Redirects',
    href: '/admin/redirects',
    icon: ArrowRightLeft,
    requiredRole: 'editor',
    description: 'URL management',
  },
  {
    title: 'Audit Log',
    href: '/admin/audit',
    icon: History,
    requiredRole: 'admin',
    description: 'Activity history',
  },
];

const AdminSidebar = () => {
  const location = useLocation();
  const { user, role, signOut, hasPermission } = useAuth();

  const filteredNavItems = navItems.filter((item) => {
    if (!item.requiredRole) return true;
    return hasPermission(item.requiredRole);
  });

  const initials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : user?.email?.charAt(0).toUpperCase() || '?';

  const displayName = user?.user_metadata?.full_name || user?.email || 'User';

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r bg-sidebar-background flex flex-col">
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b px-5">
        <Link to="/admin" className="flex items-center gap-2.5 group">
          <img
            src={logo}
            alt="XperfoMax"
            width={28}
            height={16}
            className="h-5 w-auto"
          />
          <div>
            <span className="font-semibold text-sm text-sidebar-foreground">XperfoMax</span>
            <span className="text-[10px] text-muted-foreground ml-1.5 uppercase tracking-wider">CMS</span>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-0.5">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.href;

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-all group relative',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                )}
              >
                <item.icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-sidebar-foreground')} />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{item.title}</span>
                </div>
                {isActive && (
                  <ChevronRight className="h-3.5 w-3.5 opacity-60" />
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User section */}
      <div className="border-t p-3 space-y-2">
        <div className="flex items-center gap-3 px-2 py-1.5">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-primary font-semibold text-xs">{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-sidebar-foreground">{displayName}</p>
            <p className="text-[11px] text-muted-foreground capitalize">{role || 'User'}</p>
          </div>
        </div>
        <button
          onClick={signOut}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
